/**
 * common/lib/piiCipher.js — Frontend PII decryption for API responses
 *
 * Decrypts fields encrypted by bookingbash-api using AES-256-CBC.
 * 
 * Backend encryption format: "<ivHex>:<ciphertextHex>"
 * - Algorithm: AES-256-CBC
 * - Key: SHA-256 hash of SITE_PII_KEY environment variable
 * - Each encrypted value has its own random IV prepended
 *
 * IMPORTANT: The SITE_PII_KEY environment variable must match between
 * API and frontend servers. Never expose this to the browser.
 *
 * Usage:
 *   import { decryptResponsePII } from '../common/lib/piiCipher';
 *   const decryptedData = decryptResponsePII(apiResponse);
 */

import CryptoJS from 'crypto-js';

// List of PII fields that are encrypted in API responses
// Must match ENCRYPTION_FIELDS and GETSITE_PII_FIELDS from backend
const PII_FIELD_NAMES = new Set([
  // Identity
  'first_name', 'last_name', 'middle_name', 'booker_name', 'display_name',
  'firstName', 'lastName', 'firstname', 'lastname', 'agent_email',
  'customer_last_name', 'customer_first_name', 'customer_email',
  
  // Email
  'email', 'email_to_trv', 'assist_bookby_email',
  
  // DOB
  'dateOfBirth', 'dob', 'date_of_birth', 'anniversary_date',
  'passport_exp', 'passport_issue_date',
  
  // Contact
  'mobile_number', 'customer_mobile', 'phone_no', 'contact_no',
  'mobile', 'phone', 'isd_code', 'country_code',
  
  // Address
  'address', 'address2', 'billing_address', 'street', 'pincode',
  'zip_code', 'zip', 'landmark',
  
  // Travel Documents
  'passport_no', 'passport_number', 'pan_no', 'pan', 'national_id',
  'driving_lincense_number',
  
  // Credentials
  'password', 'fe_password', 'mo_password', 'otp', 'otp_data',
  
  // Financial
  'card', 'cardNumber', 'cvv',
  
  // GetSite fields (site configuration)
  'domain_name', 'org_id', 'site_key', 'display_website_currency',
  'passowrd_length', 'client_code',
  'support_emailid', 'support_number', 'pg_details', 'loyalty',
  'loyalty_details'
]);

// Passphrase must match backend's SITE_PII_KEY environment variable
// Server-side only - never expose to browser
const PASSPHRASE = process.env.SITE_PII_KEY;

// Derive the same 32-byte key using SHA-256 (matching backend)
const ENCRYPTION_KEY = CryptoJS.SHA256(PASSPHRASE);

// Master switch: PII decryption only runs when PII_ENCRYPTION_ENABLED=true.
// Must match the backend flag so the API encrypts and the FE decrypts together.
const ENCRYPTION_ENABLED = String(process.env.PII_ENCRYPTION_ENABLED).toLowerCase() === 'true';

/**
 * Decrypt a single encrypted field value.
 * Supports double-decryption for fields that are encrypted twice.
 * 
 * @param {string} encryptedValue - Format: "<ivHex>:<ciphertextHex>"
 * @returns {*} Decrypted and JSON-parsed value, or null on error
 */
function decryptField(encryptedValue) {
  try {
    if (!encryptedValue || typeof encryptedValue !== 'string') {
      return encryptedValue;
    }

    // Parse the IV:ciphertext format
    const parts = encryptedValue.split(':');
    if (parts.length !== 2) {
      // Not in expected format, return as-is
      return encryptedValue;
    }

    const [ivHex, ciphertextHex] = parts;

    // Convert hex strings to WordArrays
    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const ciphertext = CryptoJS.enc.Hex.parse(ciphertextHex);

    // Create cipher params object
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: ciphertext
    });

    // Decrypt using AES-256-CBC
    const decrypted = CryptoJS.AES.decrypt(cipherParams, ENCRYPTION_KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // Convert to UTF-8 string
    const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedStr) {
      console.error('[piiCipher] Decryption resulted in empty string');
      return encryptedValue;
    }

    // Parse JSON (backend stringifies all values before encryption)
    let result = JSON.parse(decryptedStr);
    
    // Check if result is still encrypted (double encryption)
    // Format: "ivHex:ciphertextHex"
    if (typeof result === 'string' && result.includes(':')) {
      const doubleCheckParts = result.split(':');
      if (doubleCheckParts.length === 2 && 
          /^[0-9a-f]+$/i.test(doubleCheckParts[0]) && 
          /^[0-9a-f]+$/i.test(doubleCheckParts[1])) {
        // Data is double-encrypted, decrypt again
        console.log('[piiCipher] Double encryption detected, decrypting again...');
        return decryptField(result);
      }
    }
    
    return result;
  } catch (error) {
    console.error('[piiCipher] Decryption error:', error);
    // Return original value on error rather than breaking the app
    return encryptedValue;
  }
}

/**
 * Recursively walk any API response and decrypt all PII fields.
 * 
 * Safe to call on any JSON-serializable value:
 *   - Arrays  → each element is processed
 *   - Objects → matching keys are decrypted, others are recursed
 *   - Primitives → returned as-is
 * 
 * @param {*} data - API response data
 * @returns {*} Data with all PII fields decrypted
 */
function decryptResponsePII(data) {
  if (!ENCRYPTION_ENABLED) return data;
  if (Array.isArray(data)) {
    return data.map(decryptResponsePII);
  }
  
  if (data !== null && typeof data === 'object') {
    const result = {};
    for (const [key, val] of Object.entries(data)) {
      if (PII_FIELD_NAMES.has(key) && val !== null && val !== undefined) {
        // Decrypt this field
        result[key] = decryptField(val);
      } else {
        // Recurse into nested objects/arrays
        result[key] = decryptResponsePII(val);
      }
    }
    return result;
  }
  
  return data;
}

/**
 * Decrypt a site record from getsite API response.
 * Convenience wrapper for decryptResponsePII.
 * 
 * @param {Object} siteRecord
 * @returns {Object}
 */
function decryptSiteRecord(siteRecord) {
  return decryptResponsePII(siteRecord);
}

export { decryptField, decryptResponsePII, decryptSiteRecord };
